import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { productsApi, type ProductDetails, type ProductSummary } from '../api/productsApi'

export interface ProductListParams {
  page?: number
  query?: string
  taxonomyId?: number | null
  termId?: number | null
  status?: ProductSummary['status'] | null
  pageSize?: number
  force?: boolean
}

export function productListKey(params?: Partial<ProductListParams>): string {
  const page = params?.page ?? 1
  const query = (params?.query ?? '').trim().toLowerCase()
  const taxonomyId = params?.taxonomyId ?? 'all'
  const termId = params?.termId ?? 'all'
  const status = params?.status ?? 'all'
  const pageSize = params?.pageSize ?? 20
  return `${taxonomyId}:${termId}:${query}:${status}:${page}:${pageSize}`
}

export interface ProductsState {
  listCache: Record<string, { products: ProductSummary[]; total: number }>
  loadingList: boolean
  activeProducts: ProductSummary[] | null
  loadingActive: boolean
  detailsById: Record<number, ProductDetails>
  loadingById: Record<number, boolean>
}

const initialState: ProductsState = {
  listCache: {},
  loadingList: false,
  activeProducts: null,
  loadingActive: false,
  detailsById: {},
  loadingById: {},
}

export const fetchProductsList = createAsyncThunk(
  'catalogProducts/fetchList',
  async (params: ProductListParams) => {
    const page = params.page ?? 1
    const query = params.query ?? ''
    const taxonomyId = params.taxonomyId ?? null
    const termId = params.termId ?? null
    const status = params.status ?? null
    const pageSize = params.pageSize ?? 20
    const result = await productsApi.listProducts(page, query, taxonomyId, termId, status, pageSize)
    const key = productListKey(params)
    return {
      key,
      products: result.products,
      total: result.pagination?.total ?? result.products.length,
    }
  },
  {
    condition: (params: ProductListParams, { getState }) => {
      if (params.force) return true
      const state = (getState() as { catalogProducts?: ProductsState }).catalogProducts
      const key = productListKey(params)
      return !state?.listCache[key] && !state?.loadingList
    },
  },
)

export const fetchActiveProducts = createAsyncThunk(
  'catalogProducts/fetchActiveProducts',
  async (_force: boolean | undefined) => {
    const result = await productsApi.listProducts(1, '', null, null, 'active', 100)
    return result.products
  },
  {
    condition: (force, { getState }) => {
      if (force) return true
      const state = (getState() as { catalogProducts?: ProductsState }).catalogProducts
      return !state?.activeProducts && !state?.loadingActive
    },
  },
)

export const fetchProductDetails = createAsyncThunk(
  'catalogProducts/fetchDetails',
  async ({ id }: { id: number; force?: boolean }) => productsApi.getProduct(id),
  {
    condition: ({ id, force }: { id: number; force?: boolean }, { getState }) => {
      const state = (getState() as { catalogProducts?: ProductsState }).catalogProducts
      return Boolean(force) || (!state?.detailsById[id] && !state?.loadingById[id])
    },
  },
)

const productsSlice = createSlice({
  name: 'catalogProducts',
  initialState,
  reducers: {
    productDetailsUpdated(state, action: PayloadAction<ProductDetails>) {
      state.detailsById[action.payload.id] = action.payload

      // Sync activeProducts
      if (state.activeProducts) {
        const index = state.activeProducts.findIndex((p) => p.id === action.payload.id)
        if (action.payload.status === 'active') {
          const summary: ProductSummary = {
            id: action.payload.id,
            name: action.payload.name,
            slug: action.payload.slug,
            summary: action.payload.summary,
            status: action.payload.status,
          }
          if (index >= 0) {
            state.activeProducts[index] = summary
          } else {
            state.activeProducts.unshift(summary)
          }
        } else if (index >= 0) {
          state.activeProducts.splice(index, 1)
        }
      }

      // Sync listCache
      for (const entry of Object.values(state.listCache)) {
        const index = entry.products.findIndex((p) => p.id === action.payload.id)
        if (index >= 0) {
          entry.products[index] = {
            id: action.payload.id,
            name: action.payload.name,
            slug: action.payload.slug,
            summary: action.payload.summary,
            status: action.payload.status,
          }
        }
      }
    },
    invalidateProductsList(state) {
      state.listCache = {}
      state.activeProducts = null
    },
    productRemoved(state, action: PayloadAction<number>) {
      delete state.detailsById[action.payload]
      if (state.activeProducts) {
        state.activeProducts = state.activeProducts.filter((p) => p.id !== action.payload)
      }
      for (const entry of Object.values(state.listCache)) {
        entry.products = entry.products.filter((p) => p.id !== action.payload)
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProductsList.pending, (state) => {
        state.loadingList = true
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        state.loadingList = false
        state.listCache[action.payload.key] = {
          products: action.payload.products,
          total: action.payload.total,
        }
      })
      .addCase(fetchProductsList.rejected, (state) => {
        state.loadingList = false
      })
      .addCase(fetchActiveProducts.pending, (state) => {
        state.loadingActive = true
      })
      .addCase(fetchActiveProducts.fulfilled, (state, action) => {
        state.loadingActive = false
        state.activeProducts = action.payload
      })
      .addCase(fetchActiveProducts.rejected, (state) => {
        state.loadingActive = false
      })
      .addCase(fetchProductDetails.pending, (state, action) => {
        state.loadingById[action.meta.arg.id] = true
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.loadingById[action.meta.arg.id] = false
        state.detailsById[action.payload.id] = action.payload
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loadingById[action.meta.arg.id] = false
      })
  },
})

export const productsActions = productsSlice.actions
export const productsReducer = productsSlice.reducer
